// The six digits that authorise one write, and the drawing of what came back.
//
// ---------------------------------------------------------------------------
// ONE FIELD, FOUR SCREENS, AND THE REASON IT IS SHARED RATHER THAN COPIED
//
// mutate.mjs already caught this class of thing once: four copies of a refusal
// block meant four mutations that stopped applying when they were consolidated,
// and the note above Refusal.tsx records it. A code field is worse than a
// refusal block to have four of, because the property that matters about it is
// negative — the code reaches React state and nothing else — and a negative
// property held in four places is one somebody weakens in three of them.
//
// ---------------------------------------------------------------------------
// WHAT IS TRUE OF THE VALUE IN THIS FIELD
//
// It is React state on the screen that owns it, it goes into one request body,
// and the owner clears it when the request comes back. It is not in the session
// store, not in a url, not in an `aria-label` and not in a log. Enrol.tsx makes
// the same claim about the enrolment secret and README.md records why that one
// is stated so carefully; this is the weaker sibling of it — a TOTP code is
// worth thirty seconds and one use rather than for ever — and it is written down
// anyway, because "it did not seem worth protecting" is how the strong one gets
// weakened by analogy later.
//
// CLEARED ON EVERY ANSWER, INCLUDING A REFUSAL, AND THAT IS NOT TIDINESS. The
// service spends the time step inside `verifyAndClaim` whether or not the
// operation that followed succeeded — freshFactor.ts names this as a real cost
// and explains why it is accepted — so a code that has been sent is spent, and a
// field that still held it would invite somebody to press the button again with
// digits that cannot work. They would then be told the code was wrong, which is
// true and reads as an accusation about their typing.
//
// ---------------------------------------------------------------------------
// NOTHING HERE DECIDES WHETHER A CODE IS NEEDED, OR WHETHER ONE IS ANY GOOD
//
// No length test, no digits-only test, and no disabled button waiting for six
// characters. Those are rules about a TOTP code and totp.ts owns them: it
// normalises what it is given and judges it against parameters stored per
// factor, so a `maxLength={6}` here would be this console asserting a period and
// a digit count that are columns on a row it has never read.
//
// An empty field is therefore sent, and the service answers
// `second_factor_code_required` with a sentence naming the field. That looks
// wasteful and is the honest behaviour: the alternative is this file deciding
// what the server requires, which is the one thing README.md says a component
// may not do, and the alternative's failure mode is a button that refuses to
// work on an instance whose rules have moved.

import { useRef, type InputHTMLAttributes, type ReactNode } from "react";
import { NoticeBox } from "@nova/desk-ui/parts";
import { FieldRow } from "@nova/desk-ui/kit";
import type { AdminRefusal } from "../api/admin.js";

/**
 * What every authenticator-code field carries so a password manager leaves it
 * alone.
 *
 * `autocomplete="off"` is a known token. Apple and Chrome ignore it on a
 * sign-in form and offer iCloud Passwords anyway — that is the overlay on
 * the login authenticator step. `one-time-code` is worse: it asks the OS
 * for an SMS or mail code, which this field is not. An unrecognised token
 * is the documented way to refuse AutoFill. The data-* attrs cover the
 * managers that do not read autocomplete.
 */
export const FACTOR_FIELD: Pick<
  InputHTMLAttributes<HTMLInputElement>,
  "autoComplete" | "autoCorrect" | "autoCapitalize" | "spellCheck"
> & {
  "data-1p-ignore": "true";
  "data-lpignore": "true";
  "data-form-type": "other";
} = {
  autoComplete: "xp-totp",
  autoCorrect: "off",
  autoCapitalize: "none",
  spellCheck: false,
  "data-1p-ignore": "true",
  "data-lpignore": "true",
  "data-form-type": "other",
};

export type AuthoriseProps = {
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** True while a request is in flight, so nobody types into a field being read. */
  readonly busy: boolean;
  /**
   * What this code will authorise, in the reader's words.
   *
   * On the label rather than only in the heading above it, because four of these
   * fields exist in this application and a screen reader moving between them
   * would otherwise hear "Authenticator code" four times with nothing to
   * distinguish them.
   */
  readonly what: string;
  /**
   * Enter on a digit box. Publish and token mint are a code then a button,
   * and the button is what a person has just finished typing toward.
   */
  readonly onEnter?: (() => void) | undefined;
};

/**
 * The code field.
 *
 * Six boxes for the six digits a TOTP code usually is. That is presentation,
 * not a rule: totp.ts still owns length and digits, an empty group is still
 * sent, and a paste of the whole code into the first box still fills them all.
 * A number input is still refused, because it strips a leading zero.
 */
export function Authorise({ value, onChange, busy, what, onEnter }: AuthoriseProps): ReactNode {
  const boxes = useRef<Array<HTMLInputElement | null>>([]);
  const cells = Array.from({ length: 6 }, (_, index) => value[index] ?? "");
  const label = `Authenticator code, to ${what}`;

  const write = (next: string, focus: number): void => {
    onChange(next.slice(0, 6));
    boxes.current[Math.max(0, Math.min(5, focus))]?.focus();
  };

  return (
    <FieldRow label={label}>
      <div className="otp" role="group" aria-label={label}>
        {cells.map((cell, index) => (
          <input
            key={index}
            ref={(node) => {
              boxes.current[index] = node;
            }}
            className="input"
            value={cell}
            inputMode="numeric"
            {...FACTOR_FIELD}
            name={`xp-factor-${index}`}
            disabled={busy}
            aria-label={`Digit ${index + 1} of 6, to ${what}`}
            onChange={(event) => {
              const incoming = event.target.value.replace(/\D/g, "");
              if (incoming.length !== 1) {
                write(incoming, incoming.length >= 6 ? 5 : incoming.length);
                return;
              }
              write(
                cells.map((held, at) => (at === index ? incoming : held)).join(""),
                index + 1,
              );
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                if (!busy) onEnter?.();
                return;
              }
              if (event.key !== "Backspace" || cells[index] !== "" || index === 0) return;
              event.preventDefault();
              write(
                cells.map((held, at) => (at === index - 1 ? "" : held)).join(""),
                index - 1,
              );
            }}
          />
        ))}
      </div>
    </FieldRow>
  );
}

/**
 * A refusal, in the service's own words, with the one thing to do about it.
 *
 * THE SEVERITY IS CHOSEN ON THE KIND AND THE SENTENCE IS NOT. Which notice class
 * a refusal gets is a presentational judgement about how alarming it is, which is
 * this file's to make; what it says is the service's, passed through whole. The
 * split matters because the two 403s that mean "type it again" would otherwise be
 * drawn in the same red as the one that means "you may never do this", and a
 * person who mistypes a digit would read it as having been locked out.
 */
export function AdminRefused({ refusal }: { refusal: AdminRefusal }): ReactNode {
  const retypeable = refusal.kind === "code_required" || refusal.kind === "code_refused";

  return (
    <NoticeBox
      kind={retypeable ? "amber" : "bad"}
      // A refusal somebody has to act on is announced; one that merely asks for
      // six more digits is not, because `alert` interrupts a screen reader
      // mid-word and this is the expected path through the screen rather than a
      // fault in it.
      alert={!retypeable}
      title={TITLES[refusal.kind]}
    >
      <p>{refusal.detail}</p>
      {refusal.kind === "malformed" && refusal.issues.length > 0 ? (
        <ul>
          {refusal.issues.map((issue) => (
            <li key={issue}>{issue}</li>
          ))}
        </ul>
      ) : null}
      <p className="quiet">{NOTHING_HAPPENED[refusal.kind]}</p>
    </NoticeBox>
  );
}

/**
 * The heading for each refusal, keyed exhaustively.
 *
 * A `Record` over the union rather than a switch with a default, so a kind added
 * to api/admin.ts stops this file compiling until somebody writes the sentence.
 * A default arm would have drawn a new refusal under whatever heading happened to
 * be nearest.
 */
const TITLES: Readonly<Record<AdminRefusal["kind"], string>> = {
  session_ended: "Sign in again",
  code_required: "Enter an authenticator code",
  code_refused: "Code not accepted",
  cannot_check: "Authenticator unavailable",
  not_permitted: "You do not have permission",
  not_offered: "This feature is not available",
  conflict: "Your copy is out of date",
  too_large: "File too large",
  malformed: "Could not save",
  unavailable: "Could not save",
  unreachable: "Could not connect",
};

/**
 * What is true about the world after each refusal.
 *
 * Said separately from the service's sentence and on every one of them, because
 * the question a person actually has after a failed write is "did it half
 * happen". Most of these are a flat no; two are not, and those two are the
 * reason this table exists rather than one shared line of reassurance.
 */
const NOTHING_HAPPENED: Readonly<Record<AdminRefusal["kind"], string>> = {
  session_ended: "No changes were saved.",
  code_required: "No changes were saved. Enter the current code and try again.",
  // The one that cannot promise the code survived, and says so rather than
  // repeating the comfortable line. It is also the case that happens most.
  code_refused: "No changes were saved. Wait for the next code, then try again.",
  cannot_check: "No changes were saved. Try again later.",
  not_permitted: "No changes were saved.",
  not_offered: "No changes were saved.",
  conflict: "Your changes were not saved. Reload before trying again.",
  too_large: "No part of the file was stored. Choose a smaller file.",
  malformed: "No changes were saved. The Console sent an invalid request.",
  unavailable: "No changes were saved. Try again.",
  // Deliberately not "nothing was written". A request that never came back may
  // have reached the service and been performed, and telling somebody it did not
  // is the one reassurance here that could be false.
  unreachable: "The request may have arrived. Check the current state before trying again.",
};
